import React, { useEffect, useState, useRef } from 'react';
import { Box } from 'rebass';
import useProjectsListener from 'hooks/useProjectsListener';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from 'store';
import ProjectInfo from 'components/projectInfo';
import RecordsHeader from 'components/recordsHeader';
import { Notes } from 'components/notes';
import useNotesListener from 'hooks/useNotesListener';
import useNotes from 'hooks/useNotes';
import useRecordsListener from 'hooks/useRecordsListener';
import useRecords from 'hooks/useRecords';
import { recordSort } from 'util/constants';
import useProjectListener from 'hooks/useProjectListener';
import AddRecord from 'components/addRecord.tsx';
import Record from 'components/record';
import { setRecords, setProject, setNotes } from 'store/dataSlice';
import { placeholderProject } from 'util/placeholder';
import Assets from 'components/asset/assets';
import Selector from 'components/selector';
import { motion, AnimatePresence } from 'framer-motion';
import { Gallery } from 'components/gallery';
import { bucketFrame } from 'util/ui';

const Project = ({ match }: any) => {
  const id = match.params.id;
  const [rSort, setRecordSort] = useState(recordSort.date);
  const [rdirection, setRDirection] = useState<'desc' | 'asc'>('asc');
  const dispatch = useDispatch();
  const [sections, setSections] = useState<string[]>([]);

  useProjectListener(id);
  const { project } = useSelector((state: AppState) => state.data);

  const { notesLoading } = useNotesListener(id);
  const { notes } = useSelector((state: AppState) => state.data);
  const { addNote, updateNote, removeNote } = useNotes(id);

  const { records } = useSelector((state: AppState) => state.data);
  const { recordsLoading } = useRecordsListener(id, rSort, rdirection);
  const { addRecord, updateRecord, removeRecord } = useRecords();

  useEffect(() => {
    return () => {
      dispatch(setRecords([]));
      dispatch(setProject(placeholderProject));
      dispatch(setNotes([]));
      dispatch(setProject(undefined));
    };
  }, []);

  if (!project) return <Box></Box>;

  return (
    <>
      <div id="modal"></div>
      <Box sx={{ maxWidth: 1376, mx: 'auto', px: ['16px', '32px'], pt: '20px', pb: '8px' }}>
        {/* UI refresh: project details always shown; the bucket toggles sit just below them. */}
        <ProjectInfo key={project.id} project={project} />
        <Box mb={'16px'} />
        <Selector onSelect={(selections) => setSections(selections)} />
        <Box mb={'16px'} />
        <div className="archy-buckets">
        <AnimatePresence>
          {sections.includes('Videos') && (
            <motion.div
              style={{ overflow: 'hidden' }}
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              transition={{ ease: 'easeInOut', duration: 0.5 }}
              exit={{ height: 0 }}
            >
              {/* One rounded bucket, like the project box above: pink bar, then the records inside. */}
              <Box sx={bucketFrame}>
                <RecordsHeader addRecord={() => addRecord(id)} loading={recordsLoading} />
                <Box sx={{ p: '12px', display: 'flex', flexDirection: 'column', gap: '10px', bg: '#F9FAFB' }}>
                  {records.map((record, index) => (
                    <Record
                      key={`record${record.id}${index}`}
                      record={record}
                      deleteRecord={() => removeRecord(record.id)}
                      updateRecord={(id, record) => updateRecord(id, record)}
                    />
                  ))}
                  {/* "+ Add record" lives in the section header; the dashed bar is only for an empty list */}
                  {records.length === 0 && <AddRecord onClick={() => addRecord(id)} />}
                </Box>
              </Box>
              <Box mb={[4]} />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {sections.includes('Assets') && (
            <motion.div
              style={{ overflow: 'hidden' }}
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              transition={{ ease: 'easeInOut', duration: 0.5 }}
              exit={{ height: 0 }}
            >
              <Assets projectID={project.id} />
              <Box mb={[4]} />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {sections.includes('Notes') && (
            <motion.div
              style={{ overflow: 'hidden' }}
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              transition={{ ease: 'easeInOut', duration: 0.5 }}
              exit={{ height: 0 }}
            >
              <Notes
                addNote={() => addNote()}
                notes={notes}
                updateNote={(note) => updateNote(note.id, note)}
                deleteNote={removeNote}
                loading={notesLoading ? true : false}
              />
              <Box mb={[4]} />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          <motion.div
            style={{ overflow: 'hidden' }}
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            transition={{ ease: 'easeInOut', duration: 0.5 }}
            exit={{ height: 0 }}
          >
            <Gallery loading={false} projectId={project.id} />
            <Box mb={[4]} />
          </motion.div>
        </AnimatePresence>
        </div>
      </Box>
    </>
  );
};

export default Project;
